import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Button } from "../utils/components/ui/button";
import { Input } from "../utils/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../utils/components/ui/dialog";
import { useWriteOffCreditBills } from "../service/reportsService";
import { useDebounce } from "use-debounce";
import {
  Loader2,
  SquareArrowOutUpRight,
  Download,
  FileText,
  Search,
} from "lucide-react";

import Loading from "../components/common/LoaderCommon";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../utils/components/ui/tabs";
import CustomerReportConsolidatedReport from "./CustomerReportConsolidatedReport";
import PaginationComponent from "../components/PaginationComponent";
import { useDownloadAllInvoice } from "../service/customerService";
import CustomerPaymentHistoryModal from "../components/customer-management/CustomerPaymentHistoryModal";
import {
  Breadcrumb,
  BreadcrumbItem,

  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../utils/components/ui/breadcrumb";
import { useGetAllInvoice } from "../service/invoiseService";

function CustomerHistory() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const writeOffMutation = useWriteOffCreditBills();

  const [writeOffInvoice, setWriteOffInvoice] = useState<any | null>(null);
  const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(false);

  type WriteOffFormInputs = {
    writeOffDate: string;
    reason: string;
    notes: string;
    amount: number;
  };

  const {
    register: registerWriteOff,
    handleSubmit: handleWriteOffSubmit,
    reset: resetWriteOffForm,
    formState: { errors: writeOffErrors },
  } = useForm<WriteOffFormInputs>({
    defaultValues: {
      writeOffDate: new Date().toISOString().split("T")[0],
      reason: "",
      notes: "",
      amount: 0,
    },
  });

  const handleOpenWriteOff = (invoice: any) => {
    setWriteOffInvoice(invoice);
    resetWriteOffForm({
      writeOffDate: new Date().toISOString().split("T")[0],
      reason: "",
      notes: "",
      amount: invoice.balanceAmount || 0,
    });
  };

  const handleCloseWriteOff = () => {
    setWriteOffInvoice(null);
    resetWriteOffForm();
  };

  const onSubmitWriteOff = (data: WriteOffFormInputs) => {
    if (!writeOffInvoice) return;

    writeOffMutation.mutate(
      {
        writeOffDate: data.writeOffDate,
        reason: data.reason,
        notes: data.notes,
        items: [
          {
            salesId: writeOffInvoice.id,
            amount: Number(data.amount),
          },
        ],
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["useGetAllInvoice"],
          });
          toast.success("Invoice written off successfully.");
          handleCloseWriteOff();
        },
        onError: (error: Error) => {
          toast.error(`Unable to write off: ${error.message}`);
        },
      }
    );
  };

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [searchKey, setSearchKey] = useState("");
  const [debouncedSearchKey] = useDebounce(searchKey, 500);

  const { mutate: downloadAllInvoices, isPending } = useDownloadAllInvoice();

  const { data, isLoading } = useGetAllInvoice({
    pageNo: currentPage,
    pageSize,
    customerId: id,
    invoiceNumber: debouncedSearchKey,
  });

  const tableData = data?.invoiceResponse || [];
  const totalElements = data?.totalElements || 0;
  const totalPages = Math.ceil(totalElements / pageSize);
  const customerData = tableData?.[0]?.customer;

  const handleDownload = () => {
    if (!id) return;
    downloadAllInvoices({
      customerId: Number(id),
      isDownload: true,
      docType: "PDF",
    });
  };

  if (isLoading) return <Loading />;

  return (
    <div className="space-y-6 p-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/">Home</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/customer">Customer</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Customer History</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Customer info card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
        <div className="flex items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#EEF3FF] flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-bold text-[#1A5EF9]">
                {(customerData?.username || "?").charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#1A2B4A]">
                {customerData?.username || "—"}
              </h2>
              <p className="text-sm text-[#8A9BB5]">Customer Profile</p>
            </div>
          </div>
          <button
            onClick={() => setPaymentHistoryOpen(true)}
            className="h-10 rounded-[10px] border border-[#EEF2FA] bg-white px-5 text-sm font-semibold text-[#43536A] hover:bg-[#F5F8FD] transition-colors"
          >
            Payment History
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Phone", value: customerData?.phoneNumber },
            { label: "Email", value: customerData?.email },
            { label: "Address", value: customerData?.address },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs font-medium text-[#8A9BB5] mb-1">{label}</p>
              <p className="text-sm font-medium text-[#1A2B4A]">
                {value || "—"}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6">
        <Tabs defaultValue="history">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <TabsList className="h-11 bg-[#F0F4FF] rounded-[12px] p-1 gap-1">
              <TabsTrigger
                value="history"
                className="h-9 px-5 rounded-[10px] text-sm font-medium text-[#52627C] data-[state=active]:bg-[#1A5EF9] data-[state=active]:text-white data-[state=active]:shadow-[0_2px_8px_rgba(26,94,249,0.25)] transition-all"
              >
                Invoice History
              </TabsTrigger>
              <TabsTrigger
                value="report"
                className="h-9 px-5 rounded-[10px] text-sm font-medium text-[#52627C] data-[state=active]:bg-[#1A5EF9] data-[state=active]:text-white data-[state=active]:shadow-[0_2px_8px_rgba(26,94,249,0.25)] transition-all"
              >
                Consolidated Report
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="history">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
              <div className="relative w-full sm:w-[320px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#91A0B8]" />
                <input
                  type="text"
                  placeholder="Search by invoice number"
                  className="h-11 w-full rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] pl-10 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                />
              </div>
              <button
                onClick={handleDownload}
                disabled={isPending}
                className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-white font-medium text-sm flex items-center gap-2 hover:bg-[#0E4FDE] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Download All
              </button>
            </div>

            {tableData.length === 0 ? (
              <div className="py-16 text-center">
                <FileText className="w-10 h-10 text-[#D0DAEE] mx-auto mb-3" />
                <p className="text-sm text-[#52627C]">No invoices found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-[16px] border border-[#E8EEF9]">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F5F8FD] text-left text-xs font-semibold text-[#31425E] uppercase tracking-wide">
                        <th className="px-5 py-3">#</th>
                        <th className="px-5 py-3">Invoice No.</th>
                        <th className="px-5 py-3">Purchase Date</th>
                        <th className="px-5 py-3 text-right">Total Amount</th>
                        <th className="px-5 py-3 text-right">Salesman Name</th>
                        <th className="px-5 py-3 text-right">
                          Amount Received
                        </th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3 text-right">Balance</th>
                        <th className="px-5 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((item: any, idx: number) => (
                        <tr
                          key={item.id}
                          className="border-t border-[#EEF2FA] text-sm text-[#43536A] hover:bg-[#FAFCFF] transition-colors"
                        >
                          <td className="px-5 py-4 text-[#8A9BB5]">
                            {idx + 1 + currentPage * pageSize}
                          </td>
                          <td className="px-5 py-4 font-semibold text-[#24344F]">
                            {item?.invoiceNumber || "—"}
                          </td>
                          <td className="px-5 py-4">
                            {item?.salesDate ||
                              item?.saleDate ||
                              item?.quotationDate ||
                              "—"}
                          </td>
                          <td className="px-5 py-4 text-right font-medium text-[#1A2B4A]">
                            {Number(item.totalAmount || 0).toFixed(3)}
                          </td>
                          <td className="px-5 py-4 text-right">
                            {item?.staffName || "—"}
                          </td>
                          <td className="px-5 py-4 text-right">
                            OMR {Number(item?.paidAmount || 0).toFixed(3)}
                          </td>
                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                item?.paymentStatus === "PAID"
                                  ? "bg-[#E6F9F0] text-[#1A9E5C]"
                                  : "bg-[#FFF7ED] text-[#E08A1E]"
                              }`}
                            >
                              {item?.paymentStatus || item?.status}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-right">
                            {Number(item?.balanceAmount || 0).toFixed(3)}
                          </td>
                          <td className="px-5 py-4">
                            {item?.balanceAmount > 0 && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/customer-history/detail/${item.id}`,
                                    )
                                  }
                                  className="p-1.5 rounded-[8px] text-[#1A5EF9] hover:bg-[#EEF3FF] transition-colors"
                                  title="Settle payment"
                                  aria-label="Settle payment"
                                >
                                  <SquareArrowOutUpRight className="w-4 h-4" />
                                </button>
                                {localStorage.getItem("role") === "ADMIN" && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleOpenWriteOff(item)}
                                    className="h-8 rounded-lg border-[#BFD1FF] px-2 text-xs text-[#1A5EF9] hover:bg-[#EEF4FF] hover:text-[#0E4FDE]"
                                  >
                                    Write Off
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end mt-4">
                  <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="report">
            <CustomerReportConsolidatedReport customerId={id} />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog
        open={!!writeOffInvoice}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseWriteOff();
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-[520px] rounded-[28px] border border-[#E8EDF3] bg-white p-0 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.45)]"
        >
          <div className="p-6 sm:p-8">
            <DialogHeader className="text-left">
              <DialogTitle className="text-2xl font-semibold tracking-tight text-[#1A5EF9]">
                Write Off Invoice
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm text-slate-500">
                Write off invoice {writeOffInvoice?.invoiceNumber || ""}. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleWriteOffSubmit(onSubmitWriteOff)} className="mt-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Write-off Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  {...registerWriteOff("writeOffDate", { required: "Date is required" })}
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
                {writeOffErrors.writeOffDate && (
                  <p className="text-red-500 text-xs">{writeOffErrors.writeOffDate.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Amount <span className="text-red-500">*</span>
                </label>
                <Input
                  type="number"
                  step="0.001"
                  {...registerWriteOff("amount", { required: "Amount is required" })}
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
                {writeOffErrors.amount && (
                  <p className="text-red-500 text-xs">{writeOffErrors.amount.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Reason <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g. Uncollectible debt"
                  {...registerWriteOff("reason", { required: "Reason is required" })}
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
                {writeOffErrors.reason && (
                  <p className="text-red-500 text-xs">{writeOffErrors.reason.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Notes
                </label>
                <textarea
                  placeholder="Additional details..."
                  {...registerWriteOff("notes")}
                  className="w-full min-h-[80px] p-3 text-sm rounded-xl border border-[#E6ECF5] bg-[#F8FAFC] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>

              <DialogFooter className="mt-8 border-t border-[#E8EDF3] pt-6 sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseWriteOff}
                  className="h-11 min-w-[112px] rounded-xl border-[#D8E1EC] px-6 text-slate-600"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={writeOffMutation.isPending}
                  className="h-11 min-w-[112px] rounded-xl bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
                >
                  {writeOffMutation.isPending ? "Submitting..." : "Submit"}
                </Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {id && (
        <CustomerPaymentHistoryModal
          customerId={id}
          open={paymentHistoryOpen}
          setOpen={setPaymentHistoryOpen}
        />
      )}
    </div>
  );
}

export default CustomerHistory;
